import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Power, PowerOff, Star, StarOff, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { ApprovalWorkflow } from '@/types/workflow';
import {
  getAllWorkflows,
  deleteWorkflow,
  activateWorkflow,
  deactivateWorkflow,
  setDefaultWorkflow,
} from '@/services/workflowService';

// Mock workflows for development when backend is unavailable
const MOCK_WORKFLOWS: ApprovalWorkflow[] = [
  {
    id: 'workflow-1',
    name: 'Standard Timesheet Approval',
    description: 'Standard two-stage approval workflow for regular timesheets',
    isActive: true,
    isDefault: true,
    isInUse: true,
    version: 1,
    createdAt: new Date('2024-01-15'),
    createdBy: 'system',
    createdByName: 'System Admin',
    totalStages: 2,
    totalApprovers: 3,
    stages: [
      {
        id: 'stage-1',
        workflowId: 'workflow-1',
        name: 'Manager Review',
        stageOrder: 1,
        approvalType: 'any',
        requiredApprovals: 1,
        approvers: [
          { id: 'emp-1', approverName: 'John Smith', approverType: 'specific', approverOrder: 1 },
          { id: 'emp-2', approverName: 'Sarah Johnson', approverType: 'specific', approverOrder: 2 }
        ]
      },
      {
        id: 'stage-2',
        workflowId: 'workflow-1',
        name: 'Director Approval',
        stageOrder: 2,
        approvalType: 'all',
        requiredApprovals: 1,
        approvers: [
          { id: 'emp-3', approverName: 'Mike Davis', approverType: 'specific', approverOrder: 1 }
        ]
      }
    ]
  },
  {
    id: 'workflow-2',
    name: 'Executive Approval',
    description: 'Three-stage approval workflow for executive timesheets',
    isActive: true,
    isDefault: false,
    isInUse: false,
    version: 1,
    createdAt: new Date('2024-02-01'),
    createdBy: 'system',
    createdByName: 'System Admin',
    totalStages: 3,
    totalApprovers: 4,
    stages: [
      {
        id: 'stage-3',
        workflowId: 'workflow-2',
        name: 'Team Lead Review',
        stageOrder: 1,
        approvalType: 'any',
        requiredApprovals: 1,
        approvers: [
          { id: 'emp-4', approverName: 'Emily Brown', approverType: 'specific', approverOrder: 1 }
        ]
      },
      {
        id: 'stage-4',
        workflowId: 'workflow-2',
        name: 'Department Manager',
        stageOrder: 2,
        approvalType: 'any',
        requiredApprovals: 1,
        approvers: [
          { id: 'emp-1', approverName: 'John Smith', approverType: 'specific', approverOrder: 1 },
          { id: 'emp-5', approverName: 'David Wilson', approverType: 'specific', approverOrder: 2 }
        ]
      },
      {
        id: 'stage-5',
        workflowId: 'workflow-2',
        name: 'Executive Approval',
        stageOrder: 3,
        approvalType: 'all',
        requiredApprovals: 1,
        approvers: [
          { id: 'emp-3', approverName: 'Mike Davis', approverType: 'specific', approverOrder: 1 }
        ]
      }
    ]
  },
  {
    id: 'NO_APPROVAL',
    name: 'No Approval Required',
    description: 'Timesheets are automatically approved without review',
    isActive: true,
    isDefault: false,
    isInUse: false,
    version: 1,
    createdAt: new Date('2024-01-01'),
    createdBy: 'system',
    createdByName: 'System',
    totalStages: 0,
    totalApprovers: 0,
    stages: []
  }
];

interface WorkflowListProps {
  onEdit: (workflow: ApprovalWorkflow) => void;
  onCreate: () => void;
  onViewHistory: (workflow: ApprovalWorkflow) => void;
  isDemo?: boolean;
  isLive?: boolean;
}

export function WorkflowList({ onEdit, onCreate, onViewHistory, isDemo = true, isLive = false }: WorkflowListProps) {
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<ApprovalWorkflow | null>(null);

  useEffect(() => {
    if (isDemo) {
      // In demo mode, use mock workflows directly
      setWorkflows(MOCK_WORKFLOWS);
      setIsLoading(false);
      setError(null);
      return;
    }
    
    // In live mode, fetch from API
    loadWorkflows();
  }, [isDemo, isLive]);

  const loadWorkflows = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAllWorkflows();
      setWorkflows(data);
    } catch (err) {
      console.error('Failed to load workflows from API:', err);
      setError('Failed to load workflows from API');
      toast.error('Failed to load workflows', {
        description: 'Could not connect to the API. Please ensure the backend is running or switch to demo mode.',
      });
      setWorkflows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;

    try {
      await deleteWorkflow(workflowToDelete.id);
      toast.success('Workflow deleted successfully');
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
      loadWorkflows();
    } catch (error: any) {
      toast.error('Cannot delete workflow (backend unavailable)');
      console.error('Delete workflow error:', error);
    }
  };

  const handleActivate = async (workflow: ApprovalWorkflow) => {
    try {
      await activateWorkflow(workflow.id);
      toast.success('Workflow activated');
      loadWorkflows();
    } catch (error: any) {
      toast.error('Cannot activate workflow (backend unavailable)');
      console.error('Activate workflow error:', error);
    }
  };

  const handleDeactivate = async (workflow: ApprovalWorkflow) => {
    try {
      await deactivateWorkflow(workflow.id);
      toast.success('Workflow deactivated');
      loadWorkflows();
    } catch (error: any) {
      toast.error('Cannot deactivate workflow (backend unavailable)');
      console.error('Deactivate workflow error:', error);
    }
  };

  const handleSetDefault = async (workflow: ApprovalWorkflow) => {
    try {
      await setDefaultWorkflow(workflow.id);
      toast.success('Default workflow updated');
      loadWorkflows();
    } catch (error: any) {
      toast.error('Cannot set default workflow (backend unavailable)');
      console.error('Set default workflow error:', error);
    }
  };

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading workflows...</div>
        </div>
      );
    }

    if (isLive && error && !isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-3">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={loadWorkflows}
              className="text-sm text-blue-600 hover:underline"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Approval Workflows</h2>
          <p className="text-muted-foreground">
            Manage approval workflows for your organization
          </p>
        </div>
        <Button onClick={onCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Workflow
        </Button>
      </div>

      {workflows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">No workflows yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first approval workflow to get started
              </p>
              <Button onClick={onCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Workflow
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {workflows.map((workflow) => (
            <Card key={workflow.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle>{workflow.name}</CardTitle>
                      {workflow.isDefault && (
                        <Badge variant="default" className="bg-yellow-500">
                          <Star className="h-3 w-3 mr-1" />
                          Default
                        </Badge>
                      )}
                      {workflow.isActive ? (
                        <Badge variant="default" className="bg-green-500">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                      {workflow.isInUse && (
                        <Badge variant="outline">In Use</Badge>
                      )}
                    </div>
                    <CardDescription>{workflow.description || 'No description'}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewHistory(workflow)}
                      title="View History"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(workflow)}
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {workflow.isActive ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeactivate(workflow)}
                        title="Deactivate"
                      >
                        <PowerOff className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleActivate(workflow)}
                        title="Activate"
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                    )}
                    {!workflow.isDefault && workflow.isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefault(workflow)}
                        title="Set as Default"
                      >
                        <StarOff className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setWorkflowToDelete(workflow);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={workflow.isInUse || workflow.isDefault}
                      title={workflow.isInUse ? 'Cannot delete workflow in use' : 'Delete'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Stages</div>
                    <div className="font-medium">{workflow.totalStages}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Approvers</div>
                    <div className="font-medium">{workflow.totalApprovers}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Version</div>
                    <div className="font-medium">v{workflow.version}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Created By</div>
                    <div className="font-medium">{workflow.createdByName || 'Unknown'}</div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-sm font-medium mb-2">Stages:</div>
                  <div className="flex flex-wrap gap-2">
                    {workflow.stages.map((stage, index) => (
                      <Badge key={stage.id || index} variant="outline">
                        {index + 1}. {stage.name} ({stage.approvers.length} approvers)
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{workflowToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
